import { Cookies } from 'react-cookie';
import { getConfig } from '@belong/configs/next/config';
import { findEntryForPromotion, getContentfulSettings, getPageContent } from '@belong/contentful';
import { checkForPromo } from '@belong/domain-nbn-core/utils';
import { platformConfig } from '@belong/env';
import { checkAuthenticationCookies } from '@belong/providers/authentication';
import type { TSSPChain } from '@belong/ssp-execution';
import { Entry, PLATFORM_TYPE } from '@belong/types';
import { withBasePath, withoutParams } from '@belong/url-utils';
import { TPublicSSPContext } from './types';

export interface IContentRetrievalEnrichment {
  statusCode?: number;
  pageEntry?: Entry<any>;
  isLoggedIn?: boolean;
  userType?: string;
}

const contentRetrievalEnrichment = async (
  contentRetrievalCtx: TPublicSSPContext
): Promise<IContentRetrievalEnrichment> => {
  const { correlationId } = contentRetrievalCtx.appServerSideProps;
  const { req, res } = contentRetrievalCtx.context;
  const pageUrl = withoutParams(contentRetrievalCtx.context.resolvedUrl);
  const pagePath = withBasePath(pageUrl);

  const isNativeApp = platformConfig.platform !== PLATFORM_TYPE.BROWSER;
  const { isLoggedIn, userType } = checkAuthenticationCookies(req?.cookies || new Cookies(), isNativeApp);

  const contentfulSettings = getContentfulSettings(
    new Cookies(req?.headers.cookie),
    getConfig().publicRuntimeConfig.contentful
  );

  const { items, statusCode } = await getPageContent({
    contentfulSettings,
    pagePath,
    correlationId
  });

  if (statusCode) {
    if (res) {
      res.statusCode = statusCode;
    }

    return {
      statusCode
    };
  }

  const pageEntry = findEntryForPromotion(items, checkForPromo(contentRetrievalCtx.context));

  return { pageEntry, isLoggedIn, userType };
};

export type AdditionalContentFetcher<T extends Record<string, any> = Record<string, any>> = (
  ctx: TPublicSSPContext
) => Promise<T>;

/**
 * If a page needs to fetch additional content which is not currentlly part of the contentful page, eg products/offers,
 * we can pass an `additionalFetcher` function. The value returned from the function will be appened to the `pageProps` object
 */
export const getContentRetrievalSSP =
  (additionalFetcher?: AdditionalContentFetcher): TSSPChain<TPublicSSPContext> =>
  async ctx => {
    const [{ statusCode, pageEntry, isLoggedIn, userType }, additionalContent] = await Promise.all([
      contentRetrievalEnrichment(ctx),
      additionalFetcher?.(ctx)
    ]);

    // Short-circuit if no entry found (404) or statusCode already set to 400+
    if (!pageEntry || (statusCode && statusCode >= 400)) {
      return {
        ...ctx,
        statusCode: statusCode && statusCode >= 400 ? statusCode : 404,
        pageEntry,
        isLoggedIn,
        userType
      };
    }

    return {
      ...ctx,
      pageProps: { ...ctx.pageProps, ...additionalContent },
      pageEntry,
      isLoggedIn,
      userType
    };
  };
